import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  PDFViewerComponent,
  PDFViewerLoadEvent,
  PDFViewerModule,
  PDFViewerPageChangeEvent,
} from '@progress/kendo-angular-pdfviewer';
import { QPointIconModule } from '@qpoint/icons';
import { QPointLoadingSpinnerComponent } from '@qpoint/loading';
import { saveAs } from 'file-saver';
import * as _ from 'lodash';
import { Subject } from 'rxjs';

@Component({
    selector: 'qpoint-pdf-renderer',
    templateUrl: './qpoint-pdf-renderer.component.html',
    styleUrls: ['./qpoint-pdf-renderer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        QPointIconModule,
        QPointLoadingSpinnerComponent,
        TranslateModule,
        NgbModule,
        PDFViewerModule
    ]
})
export class QPointPdfRendererComponent implements OnInit {
  @Input()
  public set pdfSource(source: string | ArrayBuffer) {
    this._pdfSource = source;
    if (source instanceof ArrayBuffer) {
      this.originalArrayBuffer = _.cloneDeep(source);
    }
  }

  public get pdfSource(): string | ArrayBuffer {
    return this._pdfSource;
  }

  private _pdfSource: string | ArrayBuffer;
  @Input()
  public filename: string;
  @Input()
  public defaultZoomLevel = 0.8;
  @Input()
  public currentPdfPage = 1;
  @Input()
  public startLoading: Subject<any>;
  @Input()
  public hideControls = false;
  public totalPdfPages: number;
  public pdfIsLoading: boolean;
  public currentZoomLevel = this.defaultZoomLevel;
  public isFullscreenDialogReference: NgbModalRef;
  private zoomSteps = 0.20;
  @ViewChild(PDFViewerComponent) pdfViewer: PDFViewerComponent;
  private originalArrayBuffer;

  constructor(private modalService: NgbModal) { }

  ngOnInit() {
    this.pdfIsLoading = true;
    if (this.startLoading) {
      this.startLoading.subscribe(() => this.pdfIsLoading = true);
    }
  }

  public zoomIn(): void {
    this.currentZoomLevel += this.zoomSteps;
    this.applyZoomLevel();
  }

  public zoomOut(): void {
    const canZoomOut = this.currentZoomLevel - this.zoomSteps >= this.zoomSteps;
    this.currentZoomLevel = canZoomOut ? this.currentZoomLevel - this.zoomSteps : this.zoomSteps;
    this.applyZoomLevel();
  }

  isString(val): val is string { return typeof val === 'string'; }
  isArrayBuffer(val): val is ArrayBuffer { return val instanceof ArrayBuffer; }

  public downloadDocument() {
    if (typeof(this.pdfSource) === 'string') {
      // pdfSource is a url
      saveAs(this.pdfSource, this.filename);
    } else {
      // pdfSource is a byte array
      const blob = new Blob([this.originalArrayBuffer], {type: 'text/plain;charset=utf-8'});
      saveAs(blob, this.filename);
    }
  }

  public async handleFullscreenMode() {
    if (this.isFullscreenDialogReference) {
      this.isFullscreenDialogReference.close();
    } else {
      const dialog = this.modalService.open(QPointPdfRendererComponent, { size: 'lg', windowClass: 'full-screen-dialog pdf-fullscreen' });
      const componentInstance = dialog.componentInstance as QPointPdfRendererComponent;
      componentInstance.defaultZoomLevel = 1.6;
      componentInstance.setDefaultZoom();
      componentInstance.pdfSource = this.originalArrayBuffer ? _.cloneDeep(this.originalArrayBuffer) : this.pdfSource;
      componentInstance.isFullscreenDialogReference = dialog;
    }
  }

  public applyZoomLevel(): void {
    if(this.pdfViewer) {
      (this.pdfViewer as any).zoom = (this.pdfViewer as any).pdfContext.zoom = this.currentZoomLevel;
      (this.pdfViewer as any).setZoomLevel(this.currentZoomLevel);
      (this.pdfViewer as any).reloadDocument();
    }
  }

  public setDefaultZoom(): void {
    this.currentZoomLevel = this.defaultZoomLevel;
    this.applyZoomLevel();
  }

  public callBackFn(event: PDFViewerLoadEvent): void {
    this.totalPdfPages = event.context.pdfPages.length;
    this.pdfViewer.enabledSelection = true;
    this.pdfIsLoading = false;
  }

  public onPageChange(event: PDFViewerPageChangeEvent) {
    this.currentPdfPage = event.currentPage;
  }
}
